import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, User, LogOut, LayoutDashboard, Shield } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_2b09a7d1-98b2-45b2-a3c9-7352c92f0ea0/artifacts/kpct7reh_Logo%20Transparent%20PNG-1.png';

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isPaid, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#1A365D]/10"
      data-testid="global-navbar"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src={LOGO_URL} 
              alt="KohariGonzalez Oneyear&Brown" 
              className="h-10 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {!isAuthenticated ? (
              <>
                <div className="flex items-center gap-3">
                  <Link to="/login">
                    <Button variant="ghost" data-testid="nav-sign-in-button">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                      <Button 
                        className="bg-[linear-gradient(160deg,_#00B4B4,_#2BC3C3)] text-white"
                        data-testid="nav-get-started-button"
                      >
                        Get Started
                      </Button>
                    </motion.div>
                  </Link>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-4">
                {isPaid && (
                  <Link 
                    to="/dashboard"
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      isActive('/dashboard') ? 'text-[#00B4B4]' : 'text-[#1A365D] hover:text-[#00B4B4]'
                    }`}
                  >
                    <LayoutDashboard className="h-4 w-4" />
                    Dashboard
                  </Link>
                )}
                {isAdmin && (
                  <Link 
                    to="/admin"
                    className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                      isActive('/admin') ? 'text-[#00B4B4]' : 'text-[#1A365D] hover:text-[#00B4B4]'
                    }`}
                  >
                    <Shield className="h-4 w-4" />
                    Admin
                  </Link>
                )}
                <div className="flex items-center gap-2 pl-4 border-l border-slate-200">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-[#00B4B4]/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-[#00B4B4]" />
                    </div>
                    <span className="text-sm font-medium text-[#1A365D]">
                      {user?.firstName}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={handleLogout}
                    className="text-slate-500 hover:text-red-600"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-[#1A365D]" />
            ) : (
              <Menu className="h-6 w-6 text-[#1A365D]" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-slate-100"
          >
            <div className="flex flex-col gap-3">
              {!isAuthenticated ? (
                <>
                  <Link 
                    to="/login" 
                    className="px-3 py-2 text-[#1A365D] font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/register" 
                    className="px-3"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button className="w-full bg-[#00B4B4] text-white">
                      Get Started
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  {isPaid && (
                    <Link 
                      to="/dashboard" 
                      className="flex items-center gap-2 px-3 py-2 text-[#1A365D] font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Dashboard
                    </Link>
                  )}
                  {isAdmin && (
                    <Link 
                      to="/admin" 
                      className="flex items-center gap-2 px-3 py-2 text-[#1A365D] font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Shield className="h-4 w-4" />
                      Admin
                    </Link>
                  )}
                  <button 
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-2 px-3 py-2 text-red-600 font-medium"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
}
